import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ownerApi } from './ownerClient'
import type { DashboardData, RevenueGranularity, RevenueSeries } from '../types'

/**
 * OWNER-only analytics dashboard (D-051) over `/api/dashboard` (gated to ROLE_OWNER on the backend).
 * The tenant is ambient (staff JWT + resolver); these never send a tenant id. Read-only.
 */

async function fetchDashboard(): Promise<DashboardData> {
  const { data } = await ownerApi.get<DashboardData>('/dashboard')
  return data
}

/** Summary cards (counts + revenue + top sellers). Refetches on focus + every 60s so it stays current. */
export function useDashboard(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: fetchDashboard,
    enabled,
    refetchInterval: enabled ? 60000 : false,
  })
}

async function fetchRevenueSeries(
  granularity: RevenueGranularity,
  from: string,
  to: string,
): Promise<RevenueSeries> {
  const { data } = await ownerApi.get<RevenueSeries>('/dashboard/revenue-series', {
    params: { granularity, from, to },
  })
  return data
}

/**
 * The customizable revenue series for the chart. `from`/`to` are ISO dates (yyyy-MM-dd) the caller
 * derives from the selected granularity + range preset. `keepPreviousData` avoids a flash of empty
 * chart when switching granularity/range.
 */
export function useRevenueSeries(
  granularity: RevenueGranularity,
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['dashboard', 'revenue-series', granularity, from, to],
    queryFn: () => fetchRevenueSeries(granularity, from, to),
    enabled,
    placeholderData: keepPreviousData,
  })
}
